import { GridColDef, GridRenderCellParams, GridValidRowModel } from '@mui/x-data-grid-premium'
import { ReactNode } from 'react'
import { baseColumnConfig } from '@omnea-app/fe-tables/columns/definitions/baseColumnConfig'
import { RowGroupingColumnTableCell } from '@omnea-app/fe-tables/row-grouping/components/RowGroupingColumnTableCell'
import { isDefined } from '@omnea-app/packages/universal/type-guards/isDefined'
import { getSupplierSearchFilterOperators } from './getSupplierSearchFilterOperators'
import { SupplierEditCell } from './SupplierEditCell'
import { SupplierLink } from './SupplierLink'

type SupplierRef = { id?: string | null; name?: string | null; logoUrl?: string | null } | null

type SupplierField<Row> = {
  [K in keyof Row & string]-?: Row[K] extends SupplierRef | undefined ? K : never
}[keyof Row & string]

type SupplierColumnOptions<Row extends GridValidRowModel> = {
  field: SupplierField<Row>
  headerName: string
  // Called when the row has no supplier name: the supplier is absent, or present without one
  // (e.g. requests' deleted-supplier placeholder), not only when row[field] is null.
  renderEmpty?: (params: GridRenderCellParams<Row>) => ReactNode
} & Partial<Pick<GridColDef<Row>, 'sortable' | 'editable' | 'width' | 'minWidth' | 'flex'>>

// TypeScript cannot narrow Row[SupplierField<Row>] to SupplierRef, so the read goes through one typed accessor;
// SupplierField is what guarantees the field holds a supplier.
const getSupplierRef = <Row extends GridValidRowModel>(row: Row, field: SupplierField<Row>): SupplierRef | undefined => row[field]

export const getSupplierColumn = <Row extends GridValidRowModel>({
  field,
  headerName,
  renderEmpty,
  ...layout
}: SupplierColumnOptions<Row>): GridColDef<Row> => ({
  ...baseColumnConfig,
  ...layout,
  field,
  headerName,
  valueGetter: (_, row) => getSupplierRef(row, field)?.name,
  // Row grouping hands this the raw row[field] (the supplier object), typed `never`, so read the row instead.
  groupingValueGetter: (_value, row) => getSupplierRef(row, field)?.name,
  filterOperators: getSupplierSearchFilterOperators(),
  renderCell: (params: GridRenderCellParams<Row>) => {
    if (!isDefined(params.value)) return renderEmpty?.(params)
    if (params.rowNode.type === 'group') {
      return <RowGroupingColumnTableCell columnHeaderName={headerName} value={params.value} id={params.id} />
    }
    const supplier = getSupplierRef(params.row, field)
    return (
      <SupplierLink
        supplierId={supplier?.id}
        name={params.value}
        logoUrl={supplier?.logoUrl}
        hasFocus={params.hasFocus}
        tabIndex={params.tabIndex}
      />
    )
  },
  renderEditCell: (params) => (
    <SupplierEditCell field={field} selectedSupplierId={getSupplierRef(params.row, field)?.id} id={params.id} />
  ),
})
